// pages/HoldEntry.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  PauseCircle,
  ArrowLeft,
  Save,
  RefreshCcw,
  Calendar,
  FileText,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const HoldEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const userId = localStorage.getItem("loginUserId") || "";
  
  const loanId = localStorage.getItem("LoanID") || "";
  const holdIdParam = localStorage.getItem("HoldID") || "";
  const editingHoldId = holdIdParam && holdIdParam !== "null" ? parseInt(holdIdParam) : null;

  // Form fields
  const [year, setYear] = useState("Select");
  const [month, setMonth] = useState("Select");
  const [remarks, setRemarks] = useState("");

  // Dropdown data
  const [years, setYears] = useState([]);
  const [months, setMonths] = useState([]);

  // Employee ID (needed for creating new hold)
  const [empId, setEmpId] = useState("");

  const [loading, setLoading] = useState(false);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-22/2", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // Get client IP
  const getClientIP = () => {
    return "127.0.0.1";
  };

  // Load dropdowns
  const loadDropdowns = async () => {
    try {
      const [yearRes, monthRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/hold-entry/years`),
        axios.get(`${BASE_URL}/api/hold-entry/months`),
      ]);

      if (yearRes?.data?.success) {
        setYears(yearRes.data.years || []);
      }

      if (monthRes?.data?.success) {
        setMonths(monthRes.data.months || []);
      }
    } catch (err) {
      console.error("Error loading dropdowns:", err);
    }
  };

  // Load hold details for editing
  const loadHoldDetails = async () => {
    if (!editingHoldId) return;

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/hold-entry/${editingHoldId}`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success && res.data.hold) {
        const hold = res.data.hold;
        setYear(hold.fld_Year);
        setMonth(hold.fld_Month);
        setRemarks(hold.fld_Reason || "");
      } else {
        toast.error("Hold record not found");
      }
    } catch (err) {
      toast.error("Error loading hold details");
    } finally {
      setLoading(false);
    }
  };

  // Load loan details (to get employee ID)
  const loadLoanDetails = async () => {
    if (!loanId || editingHoldId) return;

    try {
      const res = await axios.get(`${BASE_URL}/api/hold-entry/loan/${loanId}`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success && res.data.loan) {
        setEmpId(res.data.loan.fld_Employeeid);
      } else {
        toast.error("Loan not found");
      }
    } catch (err) {
      toast.error("Error loading loan details");
    }
  };

  useEffect(() => {
    loadDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editingHoldId) {
      loadHoldDetails();
    } else {
      loadLoanDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingHoldId, loanId]);

  const handleBack = () => {
    localStorage.removeItem("HoldID");
    window.location.href = "/PayRoll/HoldListing";
  };

  const handleReset = () => {
    setYear("Select");
    setMonth("Select");
    setRemarks("");
  };

  const validate = () => {
    if (!year || year === "Select") {
      toast.warn("Please Select Year");
      return false;
    }
    if (!month || month === "Select") {
      toast.warn("Please Select Month");
      return false;
    }
    if (!remarks.trim()) {
      toast.warn("Please Enter Remarks");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);

      const payload = {
        year: year,
        month: month,
        remarks: remarks,
        userId: userId,
        ipAddress: getClientIP(),
      };

      let res;
      if (editingHoldId) {
        // Update existing hold
        res = await axios.put(
          `${BASE_URL}/api/hold-entry/${editingHoldId}`,
          payload,
          { params: { finYearId: Number(finYearId) } }
        );
      } else {
        // Create new hold
        payload.branchId = branchId;
        payload.loanId = loanId;
        payload.empId = empId;

        res = await axios.post(
          `${BASE_URL}/api/hold-entry`,
          payload,
          { params: { finYearId: Number(finYearId) } }
        );
      }

      if (res?.data?.success) {
        toast.success(res.data.message);
        localStorage.removeItem("HoldID");
        setTimeout(() => {
          window.location.href = "/PayRoll/HoldListing";
        }, 1500);
      } else {
        toast.error(res?.data?.message || "Operation failed");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">HOLD ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <PauseCircle className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {editingHoldId ? "Edit Hold" : "Add New Hold"}
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {editingHoldId ? "Update existing hold record" : "Create a new hold record"}
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleBack} className={ghostBtn}>
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Form */}
          <div className={cardBase + " p-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Year */}
              <div>
                <label className={labelBase}>
                  Year <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Calendar className="w-4 h-4 text-gray-400" />
                  </div>
                  <select value={year} onChange={(e) => setYear(e.target.value)} className={`pl-11 ${inputBase}`}>
                    <option value="Select">Select</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Month */}
              <div>
                <label className={labelBase}>
                  Month <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Calendar className="w-4 h-4 text-gray-400" />
                  </div>
                  <select value={month} onChange={(e) => setMonth(e.target.value)} className={`pl-11 ${inputBase}`}>
                    <option value="Select">Select</option>
                    {months.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Remarks */}
              <div className="md:col-span-2">
                <label className={labelBase}>
                  Remarks <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 relative">
                  <div className="absolute top-3 left-0 pl-4 pointer-events-none">
                    <FileText className="w-4 h-4 text-gray-400" />
                  </div>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Enter remarks..."
                    rows={4}
                    className={`pl-11 ${inputBase}`}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6 justify-center">
              <button onClick={handleSubmit} className={primaryBtn} disabled={loading}>
                <Save className="w-4 h-4" />
                {editingHoldId ? "Update" : "Submit"}
              </button>

              <button onClick={handleReset} className={ghostBtn}>
                <RefreshCcw className="w-4 h-4" />
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default HoldEntry;